import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Loader2 } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    const target = r.user.role === "super_admin" ? "/admin/dashboard" : "/app/dashboard";
    navigate(loc.state?.from || target, { replace: true });
  };

  const fillDemo = () => {
    setEmail("demo@webcabz.in");
    setPassword("Demo@123");
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex bg-asphalt text-white relative overflow-hidden flex-col p-12">
        <div className="absolute inset-0 grid-asphalt opacity-50" />
        <Link to="/" className="relative flex items-center gap-2">
          <span className="h-8 w-8 bg-cab flex items-center justify-center rounded font-display font-black text-asphalt text-lg">W</span>
          <span className="font-display font-bold text-lg">WebCabz</span>
        </Link>
        <div className="relative mt-auto">
          <div className="text-[11px] uppercase tracking-widest text-cab font-semibold mb-3">— Operator Tools</div>
          <h2 className="font-display font-black text-5xl tracking-tighter text-balance">
            Welcome back to your <span className="text-cab">control room.</span>
          </h2>
          <p className="text-zinc-400 mt-5 max-w-md">
            Sign in to dispatch trips, allocate drivers, track expiries, and generate GST invoices — all in one place.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <h1 className="font-display font-bold text-3xl text-asphalt">Sign in</h1>
          <p className="text-zinc-500 text-sm mt-1">Welcome back. Enter your credentials below.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-zinc-300 focus:ring-2 focus:ring-cab focus:border-cab outline-none text-sm"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Password</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-zinc-300 focus:ring-2 focus:ring-cab focus:border-cab outline-none text-sm"
                placeholder="••••••••"
              />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-cab text-asphalt font-semibold py-2.5 rounded-md flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
          <div className="mt-6 pt-6 border-t border-zinc-200 text-sm text-zinc-500">
            <div className="mb-2 text-xs font-semibold text-zinc-700 uppercase tracking-wider">Try the demo</div>
            <button data-testid="demo-admin" onClick={fillDemo} className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50">
              Demo Admin
            </button>
          </div>
          <div className="mt-6 text-sm text-zinc-600">
            New to WebCabz?{" "}
            <Link to="/register" data-testid="link-to-register" className="text-asphalt font-semibold hover:text-cab">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
